import { generateActivityMapColors } from '@/modules/activities'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { VehicleRoute } from './types'
import {
  buildRegistrationNumberByVin,
  collectUsedVehicles,
  type VehicleIdentifiers,
} from './used-vehicles'

interface DrivenCoordinatesInput extends VehicleIdentifiers {
  coordinates: readonly { lat: number; lng: number; timestamp: string }[]
}

/**
 * Turns a work period's `drivenCoordinates` into per-vehicle routes, giving each
 * vehicle a stable colour and the registration number the company knows it by.
 */
export const useWorkPeriodRoute = (
  drivenCoordinates: readonly DrivenCoordinatesInput[] | undefined,
  activities: readonly VehicleIdentifiers[] | undefined,
  estimateActivities: readonly VehicleIdentifiers[] | undefined
) => {
  const translations = useTranslate({
    unknownVehicle: 'common.unknown-vehicle',
  })

  const vehicleLocations = useMemo(
    () => drivenCoordinates ?? [],
    [drivenCoordinates]
  )

  const vehicleColors = useMemo<Record<string, string>>(() => {
    const colors: Record<string, string> = {}
    const vins = vehicleLocations
      .map(location => location.vehicleVin ?? 'Unknown')
      .filter((value, index, all) => all.indexOf(value) === index)
    const palette = generateActivityMapColors(vins.length)

    vins.forEach((vin, index) => {
      colors[vin] = palette[index] ?? '#0088FF'
    })

    return colors
  }, [vehicleLocations])

  // Locations and rFMS estimates identify a vehicle by VIN only, so the plate
  // comes from the backend's vehicle lookup on the locations or from a tacho
  // activity that names both.
  const registrationNumberByVin = useMemo(
    () =>
      buildRegistrationNumberByVin([
        ...vehicleLocations,
        ...(activities ?? []),
      ]),
    [activities, vehicleLocations]
  )

  const routes = useMemo<VehicleRoute[]>(
    () =>
      vehicleLocations
        .filter(location => location.coordinates.length > 0)
        .map(location => {
          const vin = location.vehicleVin ?? null

          return {
            vehicleVin: vin,
            label:
              (vin ? registrationNumberByVin[vin] : null) ??
              vin ??
              translations.unknownVehicle,
            color: vehicleColors[vin ?? 'Unknown'] ?? '#0088FF',
            samples: location.coordinates.map(coordinate => ({
              lat: coordinate.lat,
              lng: coordinate.lng,
              timestamp: coordinate.timestamp,
            })),
          }
        }),
    [
      registrationNumberByVin,
      translations.unknownVehicle,
      vehicleColors,
      vehicleLocations,
    ]
  )

  const usedVehicles = useMemo(
    () =>
      collectUsedVehicles({
        activities: activities ?? [],
        estimateActivities: estimateActivities ?? [],
        vehicleLocations,
        vehicleColors,
        unknownVehicleLabel: translations.unknownVehicle,
      }),
    [
      activities,
      estimateActivities,
      translations.unknownVehicle,
      vehicleColors,
      vehicleLocations,
    ]
  )

  return {
    routes,
    usedVehicles,
    hasCoordinates: routes.length > 0,
  }
}
